/**
 * Flow Runner - Dev Tool
 *
 * Runs predefined match flow configurations for E2E testing.
 * Accepts a `config` search param to select a test configuration,
 * starts the flow via matchFlowTest.start, and renders FlowTestPanel.
 */

import type { FlowDefinition, FlowPlayer } from "@rivaled/shared";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { Button } from "../../../components/ui";
import { trpc } from "../../../lib/trpc";
import { FlowTestPanel } from "../../../match-flow/editor/FlowTestPanel";

type FlowRunnerSearch = {
  config: string;
};

export const Route = createFileRoute("/app/dev/flow-runner")({
  staticData: { title: "Flow Runner" },
  validateSearch: (search: Record<string, unknown>): FlowRunnerSearch => ({
    config: typeof search.config === "string" ? search.config : "minimal",
  }),
  component: FlowRunnerPage,
});

// ─────────────────────────────────────────────────────────────────────────────
// Test Configuration Types
// ─────────────────────────────────────────────────────────────────────────────

type TestConfig = {
  label: string;
  playerCount: number;
  teamCount?: number;
  teamSize?: number;
  parties?: Array<{ playerIndices: number[] }>;
  elos?: number[];
  flow: FlowDefinition;
  initialContext?: Record<string, unknown>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Flow Definition Builders
// ─────────────────────────────────────────────────────────────────────────────

function buildFlow(
  nodes: Array<{ id: string; type: string; config?: Record<string, unknown> }>,
): FlowDefinition {
  const flowNodes = nodes.map((n, i) => ({
    id: n.id,
    type: n.type,
    label: n.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    position: { x: 300, y: 50 + i * 130 },
    config: n.config ?? {},
  }));

  const edges: FlowDefinition["edges"] = nodes.slice(0, -1).map((n, i) => ({
    id: `edge_${i}`,
    source: n.id,
    target: nodes[i + 1].id,
  }));

  return {
    nodes: flowNodes,
    edges,
    startNodeId: nodes[0].id,
    version: 1,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Configurations (21 total across 5 categories)
// ─────────────────────────────────────────────────────────────────────────────

const CHARACTER_POOL = [
  { id: "char_1", name: "Warrior" },
  { id: "char_2", name: "Mage" },
  { id: "char_3", name: "Rogue" },
  { id: "char_4", name: "Healer" },
  { id: "char_5", name: "Tank" },
  { id: "char_6", name: "Archer" },
  { id: "char_7", name: "Paladin" },
  { id: "char_8", name: "Assassin" },
];

const GAME_MODES = [
  { id: "mode_1", name: "Competitive" },
  { id: "mode_2", name: "Casual" },
  { id: "mode_3", name: "Ranked" },
];

const TEST_CONFIGS: Record<string, TestConfig> = {
  // ── Category 1: Node type coverage ──────────────────────────────────────

  minimal: {
    label: "Minimal (ready_up → match_start)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "match_start_1",
        type: "match_start",
        config: { countdownSeconds: 3, displayMessage: "Match starting!" },
      },
    ]),
  },

  standard: {
    label: "Standard (ready_up → map_vote → auto_balance → match_start)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "map_vote_1",
        type: "map_vote",
        config: { timeoutSeconds: 45, tiebreaker: "random", showVotes: "count_only" },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "selection-heavy": {
    label: "Selection Heavy (map_ban → game_mode → side_select)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      {
        id: "map_ban_1",
        type: "map_ban",
        config: { timeoutSecondsPerBan: 20, bansPerTeam: 1, banOrder: "alternating" },
      },
      {
        id: "game_mode_1",
        type: "game_mode_select",
        config: { timeoutSeconds: 20, selectionMethod: "vote", gameModes: GAME_MODES },
      },
      {
        id: "side_select_1",
        type: "side_select",
        config: {
          timeoutSeconds: 20,
          selectionMethod: "coin_flip",
          sides: [
            { id: "side_a", name: "Side A" },
            { id: "side_b", name: "Side B" },
          ],
        },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "character-draft": {
    label: "Character Draft (character_ban → character_pick)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      {
        id: "char_ban_1",
        type: "character_ban",
        config: {
          timeoutSecondsPerBan: 20,
          bansPerTeam: 1,
          banOrder: "alternating",
          characterPool: CHARACTER_POOL,
        },
      },
      {
        id: "char_pick_1",
        type: "character_pick",
        config: { timeoutSecondsPerPick: 20, pickOrder: "snake", characterPool: CHARACTER_POOL },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-lobby": {
    label: "Captain Lobby (map_vote → captain_draft → host_instructions → lobby_code)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "map_vote_1", type: "map_vote", config: { timeoutSeconds: 30, tiebreaker: "random" } },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      {
        id: "host_instr_1",
        type: "host_instructions",
        config: { timeoutSeconds: 60, instructions: "Create a private lobby and share the code." },
      },
      { id: "lobby_code_1", type: "lobby_code_input", config: { timeoutSeconds: 60 } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "self-select-lobby": {
    label: "Self Select Lobby (self_select → lobby_created)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "self_select_1", type: "self_select", config: { timeoutSeconds: 45 } },
      {
        id: "lobby_created_1",
        type: "lobby_created",
        config: {
          timeoutSeconds: 60,
          lobbyUrl: "https://example.com/lobby/123",
          requireAllPlayersConfirm: true,
        },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  communication: {
    label: "Communication (voice_channel_join → team_voice_split)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      { id: "voice_join_1", type: "voice_channel_join", config: { timeoutSeconds: 30 } },
      { id: "voice_split_1", type: "team_voice_split", config: { timeoutSeconds: 30 } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  timeout: {
    label: "Timeout (ready_up → timeout → match_cancelled)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 10, requiredPercentage: 1.0 },
      },
      {
        id: "match_cancelled_1",
        type: "match_cancelled",
        config: { reason: "timeout", displayMessage: "Flow timed out." },
      },
    ]),
  },

  // ── Category 2: Party scenarios ─────────────────────────────────────────

  "captain-party-autodraft": {
    label: "Captain Party Auto-Draft (captain's party auto-assigned)",
    playerCount: 10,
    teamSize: 5,
    parties: [{ playerIndices: [0, 9] }],
    elos: [2100, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2050],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-party-pick": {
    label: "Captain Party Pick (picking player drafts whole party)",
    playerCount: 10,
    teamSize: 5,
    parties: [{ playerIndices: [2, 3] }, { playerIndices: [6, 7] }],
    elos: [1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2100],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-party-stranded": {
    label: "Captain Party Stranded (party too large → force-assign)",
    playerCount: 6,
    teamCount: 2,
    teamSize: 3,
    parties: [{ playerIndices: [0, 1, 2] }],
    elos: [2000, 1800, 1600, 1400, 1200, 1000],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-all-parties": {
    label: "Captain All Parties (all players in parties)",
    playerCount: 4,
    teamCount: 2,
    teamSize: 2,
    parties: [{ playerIndices: [0, 3] }, { playerIndices: [1, 2] }],
    elos: [2000, 1800, 1600, 1400],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-volunteer-party": {
    label: "Captain Volunteer Party (volunteer captains with parties)",
    playerCount: 10,
    teamSize: 5,
    parties: [{ playerIndices: [0, 1] }],
    elos: [1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2100],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "volunteer" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  // ── Category 3: Team count variations ───────────────────────────────────

  "three-team-balance": {
    label: "3-Team Auto Balance (9 players, 3 teams)",
    playerCount: 9,
    teamCount: 3,
    teamSize: 3,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "three-team-ban": {
    label: "3-Team Map Ban (9 players, alternating bans)",
    playerCount: 9,
    teamCount: 3,
    teamSize: 3,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      {
        id: "map_ban_1",
        type: "map_ban",
        config: { timeoutSecondsPerBan: 20, bansPerTeam: 1, banOrder: "alternating" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "four-team-self-select": {
    label: "4-Team Self Select (8 players)",
    playerCount: 8,
    teamCount: 4,
    teamSize: 2,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "self_select_1", type: "self_select", config: { timeoutSeconds: 45 } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "ffa-vote": {
    label: "FFA Map Vote (4 players, 4 teams)",
    playerCount: 4,
    teamCount: 4,
    teamSize: 1,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "map_vote_1", type: "map_vote", config: { timeoutSeconds: 30, tiebreaker: "random" } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  // ── Category 4: Player count variations ─────────────────────────────────

  "two-player": {
    label: "Two Player (minimum)",
    playerCount: 2,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "large-match": {
    label: "Large Match (10 players, full flow)",
    playerCount: 10,
    teamSize: 5,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "map_vote_1", type: "map_vote", config: { timeoutSeconds: 30, tiebreaker: "random" } },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 30, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "odd-players": {
    label: "Odd Players (5 players, 2 teams → 3+2)",
    playerCount: 5,
    teamCount: 2,
    teamSize: 3,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  // ── Category 5: Edge cases ──────────────────────────────────────────────

  "partial-ready-timeout": {
    label: "Partial Ready Timeout (50% required, trigger timeout)",
    playerCount: 4,
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 10, requiredPercentage: 0.5 },
      },
      {
        id: "match_cancelled_1",
        type: "match_cancelled",
        config: { reason: "timeout", displayMessage: "Not enough players ready." },
      },
    ]),
  },

  "map-ban-exhaust": {
    label: "Map Ban Exhaust (3 maps, 2 bans → 1 remaining)",
    playerCount: 4,
    initialContext: { mapPool: ["map_1", "map_2", "map_3"] },
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      { id: "auto_balance_1", type: "auto_balance", config: { algorithm: "snake" } },
      {
        id: "map_ban_1",
        type: "map_ban",
        config: { timeoutSecondsPerBan: 20, bansPerTeam: 1, banOrder: "alternating" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },

  "captain-draft-timeout": {
    label: "Captain Draft Timeout (trigger timeout → auto-assign)",
    playerCount: 10,
    teamSize: 5,
    elos: [1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2100],
    flow: buildFlow([
      {
        id: "ready_up_1",
        type: "ready_up",
        config: { timeoutSeconds: 30, requiredPercentage: 1.0 },
      },
      {
        id: "captain_draft_1",
        type: "captain_draft",
        config: { timeoutSecondsPerPick: 10, captainSelection: "highest_elo", draftOrder: "snake" },
      },
      { id: "match_start_1", type: "match_start", config: { countdownSeconds: 3 } },
    ]),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Page Component
// ─────────────────────────────────────────────────────────────────────────────

function FlowRunnerPage() {
  if (!import.meta.env.DEV) {
    return <Navigate to="/app" />;
  }

  return <FlowRunnerContent />;
}

function FlowRunnerContent() {
  const { config: configName } = Route.useSearch();
  const testConfig = TEST_CONFIGS[configName];

  const [matchId, setMatchId] = useState<string | null>(null);
  const [players, setPlayers] = useState<FlowPlayer[]>([]);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startMutation = trpc.matchFlowTest.start.useMutation();

  const handleStart = useCallback(async () => {
    if (!testConfig) return;
    setIsStarting(true);
    setError(null);

    try {
      const result = await startMutation.mutateAsync({
        flowDefinition: testConfig.flow,
        playerCount: testConfig.playerCount,
        parties: testConfig.parties,
        teamCount: testConfig.teamCount,
        teamSize: testConfig.teamSize,
        elos: testConfig.elos,
        initialContext: testConfig.initialContext,
      });
      setMatchId(result.matchId);
      setPlayers(result.players);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start flow");
    } finally {
      setIsStarting(false);
    }
  }, [testConfig, startMutation]);

  const handleStop = useCallback(() => {
    setMatchId(null);
    setPlayers([]);
  }, []);

  // Auto-start the flow on mount
  const [autoStarted, setAutoStarted] = useState(false);
  if (!autoStarted && testConfig && !matchId && !isStarting) {
    setAutoStarted(true);
    // Use setTimeout to avoid calling setState during render
    setTimeout(() => {
      void handleStart();
    }, 0);
  }

  if (!testConfig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface-950 gap-4">
        <h1 className="text-xl font-semibold text-text-primary">Unknown Config</h1>
        <p className="text-text-secondary">
          Config &quot;{configName}&quot; not found. Available configs:
        </p>
        <div className="flex flex-wrap gap-2 max-w-2xl justify-center">
          {Object.keys(TEST_CONFIGS).map((key) => (
            <a
              key={key}
              href={`/app/dev/flow-runner?config=${key}`}
              className="px-3 py-1.5 bg-surface-800 text-text-primary text-sm rounded-lg hover:bg-surface-700 transition-colors"
            >
              {key}
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (matchId && players.length > 0) {
    return (
      <FlowTestPanel
        matchId={matchId}
        players={players}
        flowDefinition={testConfig.flow}
        onStop={handleStop}
      />
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-surface-950 gap-4"
      data-testid="flow-runner-loading"
    >
      {isStarting ? (
        <>
          <div className="h-8 w-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-text-secondary text-sm">Starting flow: {testConfig.label}</p>
        </>
      ) : error ? (
        <>
          <p className="text-error-400 text-sm">{error}</p>
          <Button
            variant="ghost"
            onClick={() => {
              setError(null);
              void handleStart();
            }}
          >
            Retry
          </Button>
        </>
      ) : (
        <>
          <p className="text-text-secondary text-sm">Flow stopped.</p>
          <Button onClick={handleStart}>Restart</Button>
        </>
      )}
    </div>
  );
}
